import { PlasticsCalendarEvent } from '../models/plastics-calendar-event.model';
import {
  PlasticsMoldingTask,
  PlasticsMoldingTaskStatus,
} from '../models/plastics-molding-task.model';
import { RotoMachine } from '../models/roto-machine.model';
import { RotoSchedule } from '../models/roto-schedule.model';
import { calculateRotoScheduleForecast } from './calculate-roto-schedule-forecast.function';

export interface ClackScheduleProduct {
  sku: string;
  productId: string;
  partNumber: string;
  thumbnailImageUrl: string;
}

export interface ClackScheduleBlock {
  id: string;
  kind: 'clack' | 'other';
  sku: string;
  partNumber: string;
  thumbnailImageUrl: string;
  remainingQuantity: number | null;
  taskCount: number;
  durationCalendarDays: number | null;
  cumulativeCalendarDays: number | null;
  estimatedCompletionDate: string | null;
  queueIndex: number;
}

export interface ClackScheduleLane {
  id: string;
  machineName: string;
  armNumber: number;
  positionNumber: number;
  blocks: ClackScheduleBlock[];
}

export interface ClackScheduleView {
  lanes: ClackScheduleLane[];
  unscheduledTaskCount: number;
}

export function buildClackScheduleView(
  schedule: RotoSchedule,
  machines: RotoMachine[],
  moldingTasks: PlasticsMoldingTask[],
  calendarEvents: PlasticsCalendarEvent[],
  clackProducts: ClackScheduleProduct[],
  asOf: Date = new Date()
): ClackScheduleView {
  const machineById = new Map(machines.map((machine) => [machine.id, machine]));
  const taskById = new Map(moldingTasks.map((task) => [task.id, task]));
  const clackProductById = new Map(
    clackProducts.map((product) => [product.productId, product])
  );
  const scheduledTaskIds = new Set(
    schedule.productLists.flatMap((list) => list.moldingTaskIds)
  );
  const positionsByListId = getPositionsByListId(schedule);

  const forecast = calculateRotoScheduleForecast({
    asOf: asOf.toISOString(),
    productLists: schedule.productLists,
    machines: machines.map((machine) => ({
      id: machine.id,
      name: machine.name,
      approximateDailyCycleCount: machine.approximateDailyCycleCount,
      armIds: machine.arms.map((arm) => arm.id),
    })),
    tasks: moldingTasks.map((task) => ({
      id: task.id,
      productId: task.productId,
      orderedProductId: task.orderedProductId,
      totalQuantity: task.totalQuantity,
      completedQuantity: task.completedQuantity,
      status: task.status,
    })),
    calendarEvents: calendarEvents.map((event) => ({
      eventDate: new Date(event.eventDate).toISOString(),
      type: event.type,
    })),
  });
  const forecastByTaskId = new Map(
    forecast.tasks.map((task) => [task.moldingTaskId, task])
  );

  const lanes: ClackScheduleLane[] = [];

  for (const list of schedule.productLists) {
    const machine = machineById.get(list.machineId);
    if (!machine) continue;

    const queue = list.moldingTaskIds
      .map((taskId, queueIndex) => ({
        task: taskById.get(taskId),
        forecast: forecastByTaskId.get(taskId),
        queueIndex,
      }))
      .filter(
        (
          item
        ): item is {
          task: PlasticsMoldingTask;
          forecast:
            | ReturnType<typeof calculateRotoScheduleForecast>['tasks'][number]
            | undefined;
          queueIndex: number;
        } =>
          !!item.task &&
          item.task.status === PlasticsMoldingTaskStatus.IN_PROGRESS &&
          item.task.totalQuantity - item.task.completedQuantity > 0
      );
    let lastClackIndex = -1;
    for (let index = queue.length - 1; index >= 0; index--) {
      if (clackProductById.has(queue[index].task.productId)) {
        lastClackIndex = index;
        break;
      }
    }

    if (lastClackIndex < 0) continue;

    const blocks: ClackScheduleBlock[] = [];
    let previousCumulativeDays = 0;

    for (const item of queue.slice(0, lastClackIndex + 1)) {
      const clackProduct = clackProductById.get(item.task.productId);
      const cumulativeDays = item.forecast?.cumulativeCalendarDays ?? null;
      const estimatedCompletionDate =
        item.forecast?.estimatedCompletionDate ?? null;
      const durationDays =
        cumulativeDays === null
          ? null
          : cumulativeDays - previousCumulativeDays;

      if (cumulativeDays !== null) previousCumulativeDays = cumulativeDays;

      if (clackProduct) {
        blocks.push({
          id: item.task.id,
          kind: 'clack',
          sku: clackProduct.sku,
          partNumber: clackProduct.partNumber || clackProduct.sku,
          thumbnailImageUrl: clackProduct.thumbnailImageUrl,
          remainingQuantity:
            item.task.totalQuantity - item.task.completedQuantity,
          taskCount: 1,
          durationCalendarDays: durationDays,
          cumulativeCalendarDays: cumulativeDays,
          estimatedCompletionDate,
          queueIndex: item.queueIndex,
        });
        continue;
      }

      const previousBlock = blocks[blocks.length - 1];
      if (previousBlock?.kind === 'other') {
        previousBlock.taskCount++;
        previousBlock.durationCalendarDays = addDurations(
          previousBlock.durationCalendarDays,
          durationDays
        );
        previousBlock.cumulativeCalendarDays = cumulativeDays;
        previousBlock.estimatedCompletionDate = estimatedCompletionDate;
        continue;
      }

      blocks.push({
        id: `${list.id}-other-${item.queueIndex}`,
        kind: 'other',
        sku: '',
        partNumber: '',
        thumbnailImageUrl: '',
        remainingQuantity: null,
        taskCount: 1,
        durationCalendarDays: durationDays,
        cumulativeCalendarDays: cumulativeDays,
        estimatedCompletionDate,
        queueIndex: item.queueIndex,
      });
    }

    lanes.push({
      id: list.id,
      machineName: machine.name,
      armNumber: machine.arms.findIndex((arm) => arm.id === list.armId) + 1,
      positionNumber: positionsByListId.get(list.id) ?? 1,
      blocks,
    });
  }

  const machineOrder = new Map(
    [...machines]
      .sort(compareRotoMachinesForSchedule)
      .map((machine, index) => [machine.id, index])
  );
  lanes.sort((a, b) => {
    const aList = schedule.productLists.find((list) => list.id === a.id);
    const bList = schedule.productLists.find((list) => list.id === b.id);
    const machineDifference =
      (machineOrder.get(aList?.machineId ?? '') ?? Number.MAX_SAFE_INTEGER) -
      (machineOrder.get(bList?.machineId ?? '') ?? Number.MAX_SAFE_INTEGER);

    return (
      machineDifference ||
      a.armNumber - b.armNumber ||
      a.positionNumber - b.positionNumber
    );
  });

  const unscheduledTaskCount = moldingTasks.filter(
    (task) =>
      task.status === PlasticsMoldingTaskStatus.IN_PROGRESS &&
      task.totalQuantity - task.completedQuantity > 0 &&
      clackProductById.has(task.productId) &&
      !scheduledTaskIds.has(task.id)
  ).length;

  return { lanes, unscheduledTaskCount };
}

function getPositionsByListId(schedule: RotoSchedule): Map<string, number> {
  const positionCountByArm = new Map<string, number>();
  const positionsByListId = new Map<string, number>();

  for (const list of schedule.productLists) {
    const armKey = `${list.machineId}-${list.armId}`;
    const positionNumber = (positionCountByArm.get(armKey) ?? 0) + 1;
    positionCountByArm.set(armKey, positionNumber);
    positionsByListId.set(list.id, positionNumber);
  }

  return positionsByListId;
}

function addDurations(a: number | null, b: number | null): number | null {
  return a === null || b === null ? null : a + b;
}

function compareRotoMachinesForSchedule(
  a: RotoMachine,
  b: RotoMachine
): number {
  const aParts = getMachineSortParts(a.name);
  const bParts = getMachineSortParts(b.name);

  if (aParts.group !== bParts.group) return aParts.group - bParts.group;
  if (aParts.group === 0 && aParts.number !== bParts.number)
    return bParts.number - aParts.number;
  if (aParts.group === 1 && aParts.number !== bParts.number)
    return aParts.number - bParts.number;

  return aParts.name.localeCompare(bParts.name);
}

function getMachineSortParts(machineName: string): {
  group: number;
  number: number;
  name: string;
} {
  const match = machineName.trim().match(/^([a-z])\s*(\d+)/i);

  if (!match) return { group: 99, number: 0, name: machineName };

  const letter = match[1].toUpperCase();
  return {
    group: letter === 'K' ? 0 : letter === 'L' ? 1 : 99,
    number: parseInt(match[2], 10),
    name: machineName,
  };
}
